import html2canvas from 'html2canvas';

/**
 * Exports a DOM element as a PNG image.
 * Optimized for SVG-based charts (like Recharts).
 * @param element The DOM element to capture.
 * @param filename The name of the file to download (without extension).
 * @param scale Optional scale factor for resolution (default 3).
 */
export const exportElementAsPNG = async (element: HTMLElement, filename: string, scale: number = 2) => {
    try {
        // Pre-process the element to ensure visibility
        const originalDisplay = element.style.display;
        element.style.display = 'block';

        // Ensure element has an ID for onclone selector
        const originalId = element.id;
        if (!element.id) {
            element.id = `export-temp-${Date.now()}`;
        }
        const elementId = element.id;

        // Get actual rendered dimensions
        const elementRect = element.getBoundingClientRect();
        const exportWidth = Math.max(element.scrollWidth, element.offsetWidth, elementRect.width);
        const exportHeight = Math.max(element.scrollHeight, element.offsetHeight, elementRect.height);

        const canvas = await html2canvas(element, {
            backgroundColor: '#111827', // Dark background for dark mode consistency
            scale: scale, // Balanced resolution (2x for good quality without huge file sizes)
            logging: false,
            useCORS: true, // Handle cross-origin images
            allowTaint: true, // Allow cross-origin content to be rendered
            foreignObjectRendering: false, // Disable foreignObject rendering for better SVG support
            width: exportWidth,
            height: exportHeight,
            windowWidth: exportWidth,
            windowHeight: exportHeight,
            // Ignore certain elements that shouldn't be in export
            ignoreElements: (element) => {
                // Skip export buttons and hover-only UI elements
                if (element.classList.contains('export-ignore') ||
                    element.getAttribute('role') === 'tooltip' ||
                    element.classList.contains('opacity-0')) {
                    return true;
                }
                return false;
            },

            // Pre-process cloned DOM to ensure SVG elements are properly captured
            onclone: (clonedDoc) => {
                const clonedElement = clonedDoc.getElementById(elementId) || clonedDoc.body;

                // Make all hidden hover elements visible for export
                const hoverElements = clonedElement.querySelectorAll('.opacity-0, [class*="hover:opacity"]');
                hoverElements.forEach((el: any) => {
                    el.style.opacity = '0';
                    el.style.display = 'none';
                });

                // Ensure all SVG elements have proper dimensions and viewBox
                const svgs = clonedElement.querySelectorAll('svg');
                svgs.forEach((svg) => {
                    const rect = svg.getBoundingClientRect();

                    // Set explicit dimensions
                    if (!svg.hasAttribute('width') || svg.getAttribute('width') === '100%') {
                        svg.setAttribute('width', Math.max(rect.width, 100).toString());
                    }
                    if (!svg.hasAttribute('height') || svg.getAttribute('height') === '100%') {
                        svg.setAttribute('height', Math.max(rect.height, 100).toString());
                    }

                    // Ensure viewBox is set for proper scaling
                    if (!svg.hasAttribute('viewBox')) {
                        const width = parseFloat(svg.getAttribute('width') || '0');
                        const height = parseFloat(svg.getAttribute('height') || '0');
                        if (width && height) {
                            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
                        }
                    }

                    // Ensure SVG has proper styling
                    svg.style.display = 'block';
                    svg.style.overflow = 'visible';
                });

                // Ensure ResponsiveContainer divs don't interfere
                const responsiveContainers = clonedElement.querySelectorAll('.recharts-responsive-container');
                responsiveContainers.forEach((container: any) => {
                    container.style.position = 'relative';
                    container.style.width = '100%';
                    container.style.height = '100%';
                    container.style.display = 'block';
                });

                // Ensure all text elements are visible with proper fonts
                const textElements = clonedElement.querySelectorAll('text, tspan');
                textElements.forEach((text: any) => {
                    // Ensure font is defined
                    const computedStyle = window.getComputedStyle(text);
                    text.style.fontFamily = text.style.fontFamily || computedStyle.fontFamily || 'sans-serif';
                    text.style.fontSize = text.style.fontSize || computedStyle.fontSize || '12px';
                    text.style.fill = text.style.fill || text.getAttribute('fill') || 'currentColor';

                    // Make sure text is visible
                    text.style.visibility = 'visible';
                    text.style.display = 'inline';
                });

                // Ensure legend elements are visible
                const legends = clonedElement.querySelectorAll('.recharts-legend-wrapper, .recharts-default-legend');
                legends.forEach((legend: any) => {
                    legend.style.display = 'block';
                    legend.style.visibility = 'visible';
                    legend.style.opacity = '1';
                });

                // Ensure all path elements (chart lines, bars, etc.) are visible
                const paths = clonedElement.querySelectorAll('path, rect, circle, line, polygon');
                paths.forEach((shape: any) => {
                    shape.style.visibility = 'visible';
                    // Ensure stroke and fill are preserved
                    const computedStyle = window.getComputedStyle(shape);
                    if (shape.getAttribute('stroke')) {
                        shape.style.stroke = shape.getAttribute('stroke');
                    }
                    if (shape.getAttribute('fill')) {
                        shape.style.fill = shape.getAttribute('fill');
                    }
                });

                // Remove any loading indicators or export buttons
                const elementsToRemove = clonedElement.querySelectorAll('[title="Export Chart"], .export-ignore, .animate-spin');
                elementsToRemove.forEach((el: any) => el.remove());
            }
        });

        // Restore original display and ID
        element.style.display = originalDisplay;
        if (!originalId) {
            element.removeAttribute('id');
        }

        // Create download link
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL('image/png', 1.0); // Maximum quality
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error('Export failed:', error);
        throw error;
    }
};

/**
 * Exports data as a CSV file.
 * @param data Array of objects representing the data.
 * @param filename The name of the file to download (without extension).
 */
export const exportDataAsCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    // Extract headers from the first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
        headers.join(','), // Header row
        ...data.map(row => headers.map(header => {
            const value = row[header];
            // Handle strings with commas or quotes
            if (typeof value === 'string') {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        }).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${Date.now()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
